import {
    useGetAllPortfoliosEnergySummary,
    useGetAllPortfoliosValueOverview,
    useGetAllPortfoliosValueOverviewExportFile,
} from 'ClientPortal/src/api/Queries/value-overview'
import {
    decimalToLocalizedString,
    getFormattedValueAndUnitFromAreaValue,
    getFormattedValueAndUnitFromMonetaryValue,
    getFormattedValueAndUnitFromNumber,
    getFormattedValueAndUnitFromPercentage,
    getFormattedValueAndUnitFromWault,
    MonetaryValue,
    tokens,
} from 'ComponentLibrary'
import { isEmpty, sumBy } from 'lodash'

import { useSelectedPortfolio } from './useSelectedPortfolio'
import { GroupedAssets } from 'ComponentLibrary/src/widgets/GeographicPortfolio/GeographicPortfolio'
import { useNavigate } from 'react-router-dom'
import { useFlags } from 'ClientPortal/src/context/FeatureFlagContext'
import { isValueOverviewTenantSummaryItem } from 'ClientPortal/src/api/valuationModels'
import { assembleAddressTitle, assembleAssetCityWithCustomerReference } from 'ComponentLibrary/src/common/address'
import { downloadFileFromLink } from 'ClientPortal/src/pages/utils'
import { isParkingType } from 'ComponentLibrary/src/common/tenant'
import { buildDashboardHistorySeries, shouldShowNormalizedDashboardHistory } from './history-chart-data'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export function useDashboardData() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tTenantUsageTypes } = useTranslation('terminology', { keyPrefix: 'common.tenantUsageTypes' })
    const { t: tAssetListPage } = useTranslation('pages', { keyPrefix: 'assets.assetListPage' })
    const locale = useCurrentResolvedLocale()
    const { isActualRentInChartsEnabled, isEnergySummaryEnabled } = useFlags()

    const { multiPortfolioValueOverview, isMultiPortfolioValueOverviewPending } = useGetAllPortfoliosValueOverview({
        throwOnError: true,
        retry: false,
    })

    const { multiPortfolioEnergySummary, isMultiPortfolioEnergySummaryLoading } = useGetAllPortfoliosEnergySummary({
        throwOnError: true,
        retry: false,
        enabled: isEnergySummaryEnabled,
    })

    const { selectedPortfolioID, setSelectedPortfolioID } = useSelectedPortfolio()

    const { generateValueOverviewExportFileUrl, isGenerateValueOverviewExportFileUrl } = useGetAllPortfoliosValueOverviewExportFile({
        selectedPortfolioId: selectedPortfolioID,
        onSuccess: downloadFileFromLink,
    })

    const navigate = useNavigate()

    const selectedPortfolio = multiPortfolioValueOverview?.portfolios.find(({ uuid }) => uuid === selectedPortfolioID)
    const selectedEnergySummary = multiPortfolioEnergySummary?.portfolios.find(
        ({ portfolio_uuid }) => portfolio_uuid === selectedPortfolioID
    )
    // If we have a selected portfolio get summary, history etc. from there, otherwise use the full multiPortfolioValueOverview
    const { summary, history, normalized_history, tenant_summary } = selectedPortfolio || multiPortfolioValueOverview || {}
    const { energy_summary } = selectedEnergySummary || multiPortfolioEnergySummary || {}
    const portfolios = multiPortfolioValueOverview?.portfolios || []
    const showNormalizedHistory = shouldShowNormalizedDashboardHistory(normalized_history)
    // TODO: Replace with real data
    const customerData = { name: 'Placeholder', company: 'Placeholder GmbH' }

    const getMarketDevelopment = () =>
        buildDashboardHistorySeries({
            history,
            normalizedHistory: normalized_history,
            trimLeadingZeroes: true,
            series: [{ id: tCommon('marketValue'), color: tokens.ColorGraphBinaryPositiveMedium, metric: 'market_value' }],
        })

    const getIncomeDevelopment = () =>
        buildDashboardHistorySeries({
            history,
            normalizedHistory: normalized_history,
            trimLeadingZeroes: true,
            series: isActualRentInChartsEnabled
                ? [
                      {
                          id: tCommon('contractRent'),
                          color: tokens.ColorGraphSequentialTwoLight,
                          metric: 'actual_rental_income',
                          showTrends: false,
                      },
                      { id: tCommon('marketRent'), color: tokens.ColorGraphSequentialTwoMedium, metric: 'rental_income' },
                  ]
                : [{ id: tCommon('marketRent'), color: tokens.ColorGraphSequentialTwoMedium, metric: 'rental_income' }],
        })

    const getKeyValueData = () => {
        const {
            market_value,
            rental_income,
            vacancy,
            wault: rawWault,
            gross_yield,
            number_of_assets,
            number_of_units,
            rentable_area,
            actual_rental_income,
        } = summary || {}

        const parkingUnits = tenant_summary
            ? sumBy(Object.values(tenant_summary), (item) =>
                  isValueOverviewTenantSummaryItem(item) && isParkingType(item.type) ? item.number_of_units : 0
              )
            : undefined

        const totalUnits = isValueOverviewTenantSummaryItem(tenant_summary?.total) ? tenant_summary.total.number_of_units : undefined

        const nonParkingUnits = totalUnits !== undefined && parkingUnits !== undefined ? totalUnits - parkingUnits : totalUnits

        return {
            marketValue: getFormattedValueAndUnitFromMonetaryValue(market_value?.value, locale),
            marketRent: getFormattedValueAndUnitFromMonetaryValue(rental_income?.value, locale),
            actualRentalIncome: getFormattedValueAndUnitFromMonetaryValue(actual_rental_income?.value, locale),
            assets: {
                value: number_of_assets?.value?.toString() ?? '-',
                trend: number_of_assets?.trend,
            },
            units: {
                value: nonParkingUnits !== undefined ? decimalToLocalizedString(nonParkingUnits, true, 5, undefined, locale) : undefined,
                trend: number_of_units?.trend,
                parkingUnits: parkingUnits !== undefined ? decimalToLocalizedString(parkingUnits, true, 5, undefined, locale) : undefined,
            },
            rentableArea: getFormattedValueAndUnitFromAreaValue(rentable_area?.value, locale),
            vacancy: {
                ...getFormattedValueAndUnitFromPercentage(vacancy?.value, undefined, locale),
                trend: vacancy?.trend,
                trendUnit: '%-Pkt.',
            },
            wault: getFormattedValueAndUnitFromWault(
                rawWault,
                {
                    unknown: tCommon('unknown'),
                    unlimited: tCommon('unlimited'),
                    year: tCommon('year'),
                    years: tCommon('years'),
                },
                locale
            ),
            isWaultUnknown: rawWault === undefined,
            grossYield: getFormattedValueAndUnitFromPercentage(gross_yield?.value, undefined, locale),
        }
    }

    const getUsageTypeSplit = () => {
        const rentalIncomeSummary = Object.values(tenant_summary || {})
        return rentalIncomeSummary
            .filter(isValueOverviewTenantSummaryItem)
            .map((summary) => ({
                type: summary.type || 'other',
                label: tTenantUsageTypes(summary.type || 'other'),
                value: MonetaryValue.fromMonetaryValueDataObject(summary.gross_rental_income?.total).toDecimal(),
            }))
            .filter(({ type }) => type !== 'total')
    }

    const getPortfolioAssetsLocations = () =>
        (multiPortfolioValueOverview?.portfolios
            .filter((portfolio) => !(portfolio.type === 'synthetic' && isEmpty(portfolio.assets)))
            .map(({ assets, title, uuid, type }) => ({
                id: uuid,
                label: type === 'synthetic' ? tAssetListPage('syntheticPortfolioTitle') : title,
                assets: assets.map(({ address, title_image, valuation, uuid, customer_asset_reference }) => {
                    if (address.coordinates !== undefined) {
                        return {
                            image: title_image,
                            market_value: valuation.market_value,
                            value_per_sqm: valuation.value_per_rentable_area,
                            date: valuation.latest_valuation_date,
                            address,
                            customer_asset_reference,
                            coordinates: address.coordinates,
                            navigateToAsset: () => navigate(`/assets/${uuid}`),
                            assetId: uuid,
                        }
                    }
                }),
            })) as GroupedAssets[]) || []

    const getMarketValueBreakdown = () => {
        return (
            (selectedPortfolio ? [selectedPortfolio] : portfolios).map(({ assets, title, summary }) => ({
                name: title,
                children: assets.map(({ address, valuation, customer_asset_reference, uuid }) => {
                    const assetDecimalValue = valuation.market_value.value.amount
                    const portfolioDecimalValue = summary.market_value.value.amount
                    const percentage = portfolioDecimalValue > 0 ? assetDecimalValue / portfolioDecimalValue : 0
                    return {
                        labelStreetAndHouseNumber: assembleAddressTitle({ ...address }),
                        value: valuation.market_value.value,
                        trend: valuation.market_value.trend,
                        percentage,
                        href: `/assets/${uuid}/overview`,
                        labelCityAndReference: assembleAssetCityWithCustomerReference({ city: address.city, customer_asset_reference }),
                    }
                }),
            })) || []
        )
    }

    const getEnergyKeyValues = () => {
        if (!energy_summary) {
            return {
                totalCarbonDioxideEquivalent: undefined,
                primaryPowerConsumption: undefined,
                energyEfficiencyClass: [],
                fuelType: [],
            }
        }
        return {
            totalCarbonDioxideEquivalent: getFormattedValueAndUnitFromNumber(
                energy_summary.total_carbon_dioxide_equivalent,
                'kg',
                undefined,
                locale
            ),
            primaryPowerConsumption: getFormattedValueAndUnitFromNumber(
                energy_summary.primary_power_consumption,
                tCommon('kilowattHoursPerSquareMeterPerYear'),
                undefined,
                locale
            ),
            energyEfficiencyClass: energy_summary.energy_efficiency_class_summary,
            fuelType: energy_summary.fuel_type,
        }
    }

    return {
        customerData,
        getKeyValueData,
        getMarketDevelopment,
        getIncomeDevelopment,
        getPortfolioAssetsLocations,
        getUsageTypeSplit,
        getMarketValueBreakdown,
        getEnergyKeyValues,
        showNormalizedHistory,
        isEnergySummaryLoading: isMultiPortfolioEnergySummaryLoading,
        isDashboardDataPending: isMultiPortfolioValueOverviewPending,
        selectedPortfolio: selectedPortfolioID,
        setSelectedPortfolio: setSelectedPortfolioID,
        portfolios,
        generateValueOverviewExportFileUrl,
        isGenerateValueOverviewExportFileUrl,
    }
}
